import {
  AnimatedButtonContainer,
  AnimatedButtonRow,
  Button,
} from "@babackapp/customer-ui/components/button";
import {
  Header,
  HeaderDescription,
  HeaderTitle,
} from "@babackapp/customer-ui/components/header";
import { WidgetPage } from "@babackapp/customer-ui/components/widget-page";
import { EnterIcon } from "@radix-ui/react-icons";
import { ArrowLeft } from "lucide-react";
import { useState } from "react";
import { Controller } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { useResolutionOptionsForm } from "../../hooks/use-resolution-options-form";
import { useIsMerchant } from "../../hooks/use-is-merchant";
import { usePlatform } from "../../hooks/use-platform";
import { useWidgetParams } from "../../hooks/use-widget-params";
import { useCustomText } from "@babackapp/return-widget-components/hooks/use-custom-text";
import type { CatalogProductItem } from "./catalog-product-combobox";
import { ExchangeOptions } from "./exchange-options";
import { RefundOptions } from "./refund-options";

export function ResolutionOptions() {
  const {
    form,
    onSubmit,
    context,
    updateLineItem,
    lineItemId,
    orderId,
    shopId,
    exchangeAvailable,
    exchangeCreditAvailable,
    discountAvailable,
    refundAvailable,
  } = useResolutionOptionsForm();

  const exchangeVariantId = form.watch("exchangeVariantId");
  const resolution = form.watch("resolution") as string;
  const t = useCustomText();
  const navigate = useNavigate();
  const platform = usePlatform();
  const isMerchant = useIsMerchant();
  const { shopInternalId } = useWidgetParams();
  const isExchangeWithoutVariant =
    context.kind === "exchange" && resolution === "exchange" && !exchangeVariantId;

  // Shop Now hands off to the live Shopify storefront to browse products — that
  // only works when genuinely embedded there. Outside that context (baback-office
  // /o preview, admin retractation portal), offer catalog search instead — but only
  // for merchants previewing the widget, never for real end customers, and never on
  // the shopify platform where Shop Now works normally.
  const isCatalogSearchActive = platform === "baback-office" && isMerchant;
  const [selectedCatalogProduct, setSelectedCatalogProduct] =
    useState<CatalogProductItem | null>(null);
  const isCatalogSelectionMissing =
    context.kind === "exchange" &&
    resolution === "exchange-credit" &&
    isCatalogSearchActive &&
    !selectedCatalogProduct;

  function handleFormSubmit(e: React.FormEvent<HTMLFormElement>) {
    if (selectedCatalogProduct) {
      e.preventDefault();
      navigate(`../product-selection?productId=${selectedCatalogProduct.id}`, {
        state: {
          imageUrl: selectedCatalogProduct.imageUrl,
          label: selectedCatalogProduct.title,
        },
      });
      return;
    }
    onSubmit(e);
  }

  return (
    <WidgetPage>
      <Header>
        {context.kind === "exchange" ? (
          <>
            <HeaderTitle>{t("resolutionOptions.exchange.title")}</HeaderTitle>
            <HeaderDescription>
              {t("resolutionOptions.exchange.description")}
            </HeaderDescription>
          </>
        ) : (
          <>
            <HeaderTitle>{t("resolutionOptions.refund.title")}</HeaderTitle>
            <HeaderDescription>
              {t("resolutionOptions.refund.description")}
            </HeaderDescription>
          </>
        )}
      </Header>

      <form onSubmit={handleFormSubmit}>
        {context.kind === "exchange" ? (
          <Controller
            control={form.control}
            name="resolution"
            render={({ field }) => (
              <ExchangeOptions
                exchangeVariantAvailable={exchangeAvailable}
                exchangeCreditAvailable={exchangeCreditAvailable}
                value={(field.value as "exchange" | "exchange-credit") ?? null}
                onChange={(v) => {
                  field.onChange(v);
                  updateLineItem(lineItemId, { resolution: v });
                  if (v !== "exchange") {
                    form.setValue("exchangeVariantId", null);
                  }
                }}
                orderId={orderId}
                lineItemId={lineItemId}
                shopId={shopId}
                exchangeVariantId={exchangeVariantId}
                onVariantChange={(variantId) =>
                  form.setValue("exchangeVariantId", variantId)
                }
                catalogSearch={
                  isCatalogSearchActive
                    ? {
                        shopId: shopInternalId,
                        selected: selectedCatalogProduct,
                        onSelectedChange: setSelectedCatalogProduct,
                      }
                    : null
                }
              />
            )}
          />
        ) : (
          <Controller
            control={form.control}
            name="resolutionOption"
            render={({ field }) => (
              <RefundOptions
                value={field.value}
                onChange={field.onChange}
                discountAvailable={discountAvailable}
                refundAvailable={refundAvailable}
              />
            )}
          />
        )}

        <AnimatedButtonRow variants={{}}>
          <AnimatedButtonContainer
            layoutId="line-item-back-button"
            disableVariants={{ enter: true, exit: true }}
            layout="position"
          >
            <Link to="../resolution">
              <Button variant="secondary" type="button">
                <ArrowLeft />
                {t("common.back")}
              </Button>
            </Link>
          </AnimatedButtonContainer>
          <AnimatedButtonContainer
            layoutId="line-item-confirm-button"
            disableVariants={{ enter: true, exit: true }}
          >
            <Button
              type="submit"
              disabled={isExchangeWithoutVariant || isCatalogSelectionMissing}
            >
              <EnterIcon />
              {t("common.confirm")}
            </Button>
          </AnimatedButtonContainer>
        </AnimatedButtonRow>
      </form>
    </WidgetPage>
  );
}
